import { Badge } from "../ui/badge";
import {
  Building2,
  GraduationCap,
  UserCog,
  Users,
  BookOpen,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import type { LoadTestTargetPreview } from "./types";

interface TargetPreviewProps {
  preview: LoadTestTargetPreview | null;
  isLoading: boolean;
  error: string | null;
  numClients: number;
}

const Row = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-start gap-2 text-sm">
    <div className="mt-0.5 text-muted-foreground">{icon}</div>
    <div className="flex flex-col">
      <span className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-foreground">{children}</span>
    </div>
  </div>
);

const userLabel = (
  u: { name: string | null; username: string | null } | null,
): string => {
  if (!u) return "—";
  if (u.username && u.name) return `${u.name} (${u.username})`;
  return u.username || u.name || "—";
};

export const TargetPreview = ({
  preview,
  isLoading,
  error,
  numClients,
}: TargetPreviewProps) => {
  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">
          Who & what this test drives
        </span>
        {isLoading && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-destructive">
          <AlertTriangle className="h-3.5 w-3.5" />
          {error}
        </div>
      )}

      {!error && !preview && !isLoading && (
        <p className="text-xs text-muted-foreground">
          Enter a classroom ID to see its org, teacher, lesson, and students.
        </p>
      )}

      {preview && (
        <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
          <Row icon={<Building2 className="h-4 w-4" />} label="Organization">
            {preview.organization?.name ?? "—"}
          </Row>
          <Row icon={<UserCog className="h-4 w-4" />} label="Org Admin">
            {userLabel(preview.org_admin)}
          </Row>
          <Row icon={<GraduationCap className="h-4 w-4" />} label="Teacher">
            {userLabel(preview.teacher)}
          </Row>
          <Row icon={<BookOpen className="h-4 w-4" />} label="Lesson">
            {preview.lessons.length === 0 ? (
              <span className="flex items-center gap-1 text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                None assigned — test will fail
              </span>
            ) : (
              preview.lessons
                .map((l) => `${l.name ?? "(unnamed)"} (#${l.id})`)
                .join(", ")
            )}
          </Row>
          <div className="col-span-2 max-md:col-span-1">
            <Row icon={<Users className="h-4 w-4" />} label="Students (roster)">
              <span className="flex flex-wrap items-center gap-1.5">
                <Badge
                  variant={
                    preview.roster.student_count < numClients
                      ? "destructive"
                      : "secondary"
                  }
                >
                  {preview.roster.student_count} enrolled
                </Badge>
                {preview.roster.student_count < numClients && (
                  <span className="text-xs text-destructive">
                    fewer than {numClients} clients requested
                  </span>
                )}
                {preview.roster.sample_usernames.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    e.g. {preview.roster.sample_usernames.join(", ")}
                    {preview.roster.student_count >
                      preview.roster.sample_usernames.length && ", …"}
                  </span>
                )}
              </span>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};
